/**
 * Date picking for fixed-duration leave types (maternity / paternity).
 *
 * Picking a start pre-fills the statutory block (start + duration − 1,
 * inclusive), but never past `maxDate` — the booking-window cap from
 * `computeBookingWindow`, which stops at 31 Dec. Pre-filling straight past it
 * produced a range the api refuses with `booking_window_exceeded`.
 *
 * The end stays editable: a later click moves the end, an earlier (or the
 * same) click re-picks the start and pre-fills again. A moved end is clamped
 * to the full block and the cap, so the range can shrink but never overrun.
 *
 * ISO `yyyy-MM-dd` strings compare correctly as plain strings, so the
 * ordering checks below need no Date parsing.
 */
import { addDays, differenceInCalendarDays, format, parseISO } from 'date-fns'

/** Which end of the range a click set — callers close the picker on 'end'. */
export type FixedDurationPick = 'start' | 'end'

export interface FixedDurationRange {
  from: string
  to: string
  pick: FixedDurationPick
}

export function prefilledEndDate(
  start: string,
  durationDays: number,
  maxDate?: string,
): string {
  const fullEnd = format(addDays(parseISO(start), durationDays - 1), 'yyyy-MM-dd')
  if (!maxDate || fullEnd <= maxDate) return fullEnd
  return maxDate < start ? start : maxDate
}

export function nextFixedDurationRange(args: {
  clicked: string
  from: string
  durationDays: number
  maxDate?: string | undefined
}): FixedDurationRange {
  const { clicked, from, durationDays, maxDate } = args
  if (from !== '' && clicked > from) {
    const latestEnd = prefilledEndDate(from, durationDays, maxDate)
    return { from, to: clicked > latestEnd ? latestEnd : clicked, pick: 'end' }
  }
  return {
    from: clicked,
    to: prefilledEndDate(clicked, durationDays, maxDate),
    pick: 'start',
  }
}

/** Inclusive calendar-day count — maternity / paternity charge elapsed days. */
export function elapsedDays(from: string, to: string): number {
  return differenceInCalendarDays(parseISO(to), parseISO(from)) + 1
}

/**
 * Days of the block still owed when it stops at the cap (31 Dec): the
 * employee files them as a new request next year, once the new year's leave
 * credit is in. Null when the block isn't cut short by the cap — a full block,
 * no cap, or an end the user shortened themselves.
 */
export function daysLeftAfterCap(args: {
  durationDays: number
  from: string
  to: string
  maxDate?: string | undefined
}): number | null {
  const { durationDays, from, to, maxDate } = args
  if (from === '' || to === '' || maxDate === undefined || to !== maxDate) return null
  const left = durationDays - elapsedDays(from, to)
  return left > 0 ? left : null
}

/**
 * Helper line under the dates field. When the pre-fill stopped at the cap,
 * say so — otherwise a 60-day maternity block reads as a bug.
 */
export function fixedDurationNote(args: {
  label: string
  durationDays: number
  from: string
  to: string
  maxDate?: string | undefined
}): string {
  const { label, durationDays, from, to, maxDate } = args
  if (maxDate !== undefined && daysLeftAfterCap({ durationDays, from, to, maxDate }) !== null) {
    const capLabel = format(parseISO(maxDate), 'd MMM yyyy')
    return (
      `${label} leave ends on ${capLabel}, the latest date you can book right now. ` +
      `That covers ${elapsedDays(from, to)} of your ${durationDays} days.`
    )
  }
  return (
    `${label} leave is up to ${durationDays} calendar days. ` +
    'We fill in the end date for you, and you can change it.'
  )
}
